import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Route } from '../types/module-registry';

export interface SettingsConfig {
	group: string; // e.g., "User Administration", "System Configuration"
	title: string; // e.g., "User Management", "Appearance Settings"
	routes: Route[]; // Required - follows standard route conventions
	icon?: IconDefinition;
	description?: string;
	order?: number; // Optional order within group (lower numbers first)
	requiredPermission?: string | string[]; // RBAC action(s) required to see this setting
	workdOnly?: boolean; // Only visible to Workd staff users
	/**
	 * Optional sync visibility check, run on every render of the
	 * settings overview. Use this for runtime gates like a tenant
	 * feature flag that can flip without re-mounting the app — RBAC
	 * lives on `requiredPermission` instead.
	 */
	visible?: () => boolean;
	/**
	 * PD-16622 — false keeps the page's routes working (and guarded by the
	 * rules above) but leaves it out of the overview, the menu and search,
	 * for a page reached from another page, such as the website editor from
	 * a storefront's Websites tab. Defaults to listed.
	 */
	listed?: boolean;
	/**
	 * PD-16034 — words a person might type to find this page that do not
	 * appear in its title or description: synonyms, product names, jargon
	 * ("SSO", "net 30", "DKIM"). Matched case-insensitively by settings search.
	 */
	keywords?: Array<string>;
	/**
	 * PD-16034 — the cards or field groups inside the page, each with the DOM
	 * id it lands on, so search can open the page scrolled to the section.
	 */
	sections?: Array<{
		label: string;
		anchor: string;
		keywords?: Array<string>;
		/**
		 * PD-16622 — the settings path the section is on, when it is not the
		 * page's first route (e.g. `portal-sites/all?tab=web-settings`).
		 */
		path?: string;
	}>;
}

export interface SettingsGroup {
	name: string;
	settings: SettingsConfig[];
}

/**
 * PD-16033 — the order groups appear in the settings menu.
 *
 * Groups used to sort alphabetically by their English name, which put
 * one-tile groups first and the eight-tile System group last, and reordered
 * everything under a translated UI. The order is a product decision, so it is
 * declared once here; a group not in this list sorts after the listed ones,
 * alphabetically, so a module can still register a new group without editing
 * this file (it just will not be placed until someone decides where it goes).
 */
export const SETTINGS_GROUP_ORDER: ReadonlyArray<string> = [
	'Company',
	'Users & Security',
	'Sales & Customers',
	'Orders & Fulfillment',
	'Billing & Payments',
	'Communications',
	'Notifications',
	'Ecommerce',
	'Data & Layout',
	'Integrations',
	'Devices & Sensors',
];

function compareGroupNames(a: string, b: string): number {
	const ia = SETTINGS_GROUP_ORDER.indexOf(a);
	const ib = SETTINGS_GROUP_ORDER.indexOf(b);
	if (ia !== -1 && ib !== -1) {
		return ia - ib;
	}
	if (ia !== -1) {
		return -1;
	}
	if (ib !== -1) {
		return 1;
	}
	return a.localeCompare(b);
}

class SettingsRegistryClass {
	private settings = new Map<string, SettingsConfig>();
	private listeners = new Set<() => void>();

	register(moduleName: string, settingsConfigs: SettingsConfig[]) {
		if (!settingsConfigs || !Array.isArray(settingsConfigs)) {
			// Warning: Invalid settings configs for module
			return;
		}

		settingsConfigs.forEach((config, index) => {
			if (!config || typeof config !== 'object') {
				// Warning: Invalid setting config for module
				return;
			}

			// Validate that routes property exists and is an array
			if (!config.routes || !Array.isArray(config.routes)) {
				// Warning: Setting is missing routes array
				return;
			}

			const fullKey = `${moduleName}:${index}`;
			this.settings.set(fullKey, config);
		});

		this.notifyListeners();
	}

	unregister(moduleName: string) {
		const keysToDelete: string[] = [];

		for (const [key] of this.settings.entries()) {
			if (key.startsWith(`${moduleName}:`)) {
				keysToDelete.push(key);
			}
		}

		keysToDelete.forEach((key) => this.settings.delete(key));
		this.notifyListeners();
	}

	getAllSettings(): SettingsConfig[] {
		return Array.from(this.settings.values());
	}

	getSettingsByGroup(): SettingsGroup[] {
		const settingsArray = this.getAllSettings();
		const groupMap = new Map<string, SettingsConfig[]>();

		settingsArray.forEach((setting) => {
			if (!groupMap.has(setting.group)) {
				groupMap.set(setting.group, []);
			}
			groupMap.get(setting.group)!.push(setting);
		});

		return Array.from(groupMap.entries())
			.map(([name, settings]) => ({
				name,
				settings: settings.sort((a, b) => {
					// If both have order, sort by order
					if (a.order != null && b.order != null) {
						return a.order - b.order;
					}
					// If only a has order, it comes first
					if (a.order != null) {
						return -1;
					}
					// If only b has order, it comes first
					if (b.order != null) {
						return 1;
					}
					// If neither has order, sort alphabetically by title
					return a.title.localeCompare(b.title);
				}),
			}))
			.sort((a, b) => compareGroupNames(a.name, b.name));
	}

	getSetting(title: string): SettingsConfig | undefined {
		for (const [, config] of this.settings.entries()) {
			if (config.title === title) {
				return config;
			}
		}
		return undefined;
	}

	// Generate all routes for settings to be used as children of /settings route
	getAllRoutesForSettings(): Route[] {
		const routes: Route[] = [];

		this.getAllSettings().forEach((setting) => {
			// Check if setting has routes array (new format)
			if (setting.routes && Array.isArray(setting.routes)) {
				setting.routes.forEach((route) => {
					routes.push({
						...route,
						label: route.label || setting.title,
						icon: route.icon || setting.icon,
						// Default the activity-log description to "Viewed <Section
						// Title>" so /settings/<section> pages don't all collapse to
						// the parent "Viewed Settings" via inheritance. Individual
						// routes can still override by declaring their own
						// activityDescription explicitly.
						activityDescription:
							route.activityDescription ?? `Viewed ${setting.title}`,
					});
				});
			}
		});

		return routes;
	}

	subscribe(listener: () => void) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners() {
		this.listeners.forEach((listener) => listener());
	}
}

export const SettingsRegistry = new SettingsRegistryClass();
